import { GetStaticPropsContext, GetStaticPropsResult } from "next";

import { BaseProps, getBaseProps } from "@lib/utils/layout";
import { getMainMenuProps } from "@lib/header";
import { compileDefaultSEOInformation } from "@lib/utils/seo";
import { removeEmptyAttributes } from "@lib/strapi/content";
import { WebPageDTO } from "@lib/strapi/dtos/content-types/webPage.dto";
import { WebPageService } from "@lib/strapi/services/content-types/webPage.service";
import { getSectionsData, SectionsData } from "@lib/frontend/sections-data";

/**
 * Data for one CMS-driven page.
 *
 * The seven v4 page-like single types collapsed into `web-page` records (D2),
 * so every page on the site now resolves through here: fetch the record by
 * slug, resolve whatever its zones reference, compile the SEO.
 *
 * The CMS `shared.seo` component is consumed server-side and folded into the
 * compiled SEOProps — PageProps carries the compiled one, not the raw.
 */
export interface PageProps extends Omit<WebPageDTO, "seo">, BaseProps {
  sectionsData: SectionsData;
}

/** True when the page seeds aside widgets — ModularPage then runs two columns. */
export const pageHasAside = (page: unknown): boolean =>
  ((page as PageProps | undefined)?.asideSections ?? []).length > 0;

/**
 * Sections that turn the page into a form page: one band running behind the
 * whole content zone.
 *
 * The band belongs to the PAGE, not the section. Drawn by the section itself it
 * would quietly make the form the only thing that could live in the band — an
 * editor adding a markdown note beside the form would get it rendered outside,
 * unstyled, because a sibling has no way into another section's wrapper.
 */
export const BAND_LAYOUT_SECTIONS = new Set([
  // contact-form and career-form deliberately absent (owner, 2026-09-08):
  // both pages are full presentation pages now — the band tinted ALL of
  // them; the boxed form cards carry their own border + shadow
  "sections.appointment-form-section",
]);

export const pageIsBandLayout = (page: unknown): boolean =>
  (((page as PageProps | undefined)?.contentSections ?? []) as { __component?: string }[])
    .some((s) => s?.__component && BAND_LAYOUT_SECTIONS.has(s.__component));

export const getPageData = async (
  context: GetStaticPropsContext,
  slug: string
): Promise<GetStaticPropsResult<PageProps>> => {
  const locale = context.locale ?? "el";
  const page = await WebPageService.fetchBySlug(null, locale, slug);

  // A missing page is a 404, not a crash. With `en` seeded published (D12)
  // every el route has a counterpart, so this firing in practice means a seed
  // did not run — worth noticing rather than papering over.
  if (!page) {
    return { notFound: true, revalidate: 300 };
  }

  const header = await getMainMenuProps(context);
  const base = await getBaseProps(context, header);
  const sectionsData = await getSectionsData(context, page as never);

  return {
    props: removeEmptyAttributes({
      ...base,
      ...page,
      sectionsData,
      // the CMS shared.seo component overrides the derived defaults where an
      // editor has set them; anything left empty falls back to the site-wide
      // values compiled from the locale file
      seo: await compileDefaultSEOInformation(context, {
        pageTitle: (page as any).seo?.metaTitle || page.title || "",
        // an editor's metaTitle is the WHOLE title; only bare page titles
        // get the site-name suffix
        titleIsComplete: Boolean((page as any).seo?.metaTitle),
        description: (page as any).seo?.metaDescription,
        openGraphImageUrl: (page as any).seo?.metaImage?.url,
        metaRobots: (page as any).seo?.metaRobots,
        canonicalUrl: (page as any).seo?.canonicalURL,
        structuredData: (page as any).seo?.structuredData,
      }),
    }) as PageProps,
    // ~6h ISR (D51): the Google reviews aggregate refreshes without a
    // rebuild; everything else on the page is content-stable between deploys
    revalidate: 21600,
  };
};
